/**
 * Invite someone (slice 6a §6.2, artboards 71, 71b): a dialog on desktop, a
 * sheet on the phone. Their email is the only field; `already_member` and
 * `email_taken` show under it. On success it closes, and the page shows the
 * toast.
 */
import { Button } from "@crew/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { Input } from "@crew/ui/components/input";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@crew/ui/components/drawer";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useState, type FormEvent } from "react";
import { errorHint } from "../../app/api";
import { useInvite, type Sent } from "./api";

const MEMBER =
  "Talk to the same Chief of Staff in chats of their own, and see every chat, task, doc and decision. They can connect their own apps, and use the ones you share.";

type Invite = ReturnType<typeof useInvite>;

function EmailField({
  invite,
  email,
  onEmail,
}: {
  invite: Invite;
  email: string;
  onEmail: (email: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="invite-email" className="text-meta font-medium text-foreground">
        Their email
      </label>
      <Input
        id="invite-email"
        type="email"
        autoComplete="off"
        autoFocus
        required
        value={email}
        onChange={(e) => {
          onEmail(e.target.value);
          // `already_member` and `email_taken` are about the address: a new one clears them.
          if (invite.error) invite.reset();
        }}
        aria-invalid={invite.error ? true : undefined}
        className="h-10 rounded-md px-3 text-body md:text-body"
      />
      {invite.error ? (
        <p className="text-meta text-destructive" data-invite-error>
          {errorHint(invite.error)}
        </p>
      ) : null}
    </div>
  );
}

function MemberBox() {
  return (
    <div className="flex flex-col gap-1.5 rounded-md bg-muted px-4 py-3.5">
      <span className="text-meta font-semibold text-foreground">As a member, they'll</span>
      <span className="text-meta leading-sm text-copy">{MEMBER}</span>
    </div>
  );
}

function InviteForm({
  invite,
  mobile,
  onCancel,
  onSubmit,
}: {
  invite: Invite;
  mobile: boolean;
  onCancel: () => void;
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const ready = email.trim().length > 0 && !invite.isPending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onSubmit(email.trim());
  };
  const body = (
    <>
      <EmailField invite={invite} email={email} onEmail={setEmail} />
      <MemberBox />
    </>
  );
  const send = (
    <Button
      type="submit"
      size="lg"
      disabled={!ready}
      className={mobile ? "h-11 text-body" : "px-3.5"}
    >
      {invite.isPending ? "Sending…" : "Send invite"}
    </Button>
  );
  // The sheet stacks Send invite above Cancel (71b); the dialog puts it on the right (71).
  return (
    <form
      onSubmit={submit}
      className={mobile ? "flex flex-col gap-4.5" : undefined}
      data-invite-form
    >
      {mobile ? body : <div className="flex flex-col gap-4 px-6 py-5">{body}</div>}
      <div
        className={
          mobile
            ? "flex flex-col-reverse gap-2 pt-1"
            : "flex items-center justify-end gap-2 border-t border-border px-6 pt-4 pb-5"
        }
      >
        <Button
          type="button"
          variant={mobile ? "ghost" : "outline"}
          size="lg"
          className={mobile ? "h-10 text-body" : "border-input bg-card px-3.5"}
          onClick={onCancel}
          disabled={invite.isPending}
        >
          Cancel
        </Button>
        {send}
      </div>
    </form>
  );
}

export function InviteWindow({
  open,
  onOpenChange,
  workspace,
  onSent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: string;
  onSent: (out: Sent) => void;
}) {
  const mobile = useIsMobile();
  const invite = useInvite();
  const change = (next: boolean) => {
    if (!next && invite.isPending) return;
    if (!next) invite.reset();
    onOpenChange(next);
  };
  const close = () => change(false);
  const submit = (email: string) =>
    invite.mutate(email, {
      onSuccess: (out) => {
        onSent(out);
        invite.reset();
        onOpenChange(false);
      },
    });
  const form = open ? (
    <InviteForm invite={invite} mobile={mobile} onCancel={close} onSubmit={submit} />
  ) : null;
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={change} showSwipeHandle>
        <DrawerContent className="data-[swipe-direction=down]:rounded-t-2xl">
          <div className="flex flex-col gap-4.5 px-5 pt-4 pb-7">
            <div className="flex flex-col gap-1.5">
              <DrawerTitle className="font-display text-[26px] leading-7.5 font-normal tracking-tight">
                Invite someone
              </DrawerTitle>
              <DrawerDescription className="leading-5 text-pretty">
                They'll get an email with a link to join {workspace}. It works for 7 days.
              </DrawerDescription>
            </div>
            {form}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent showCloseButton={false} className="gap-0 p-0 sm:max-w-120">
        <div className="flex flex-col gap-1.5 px-6 pt-6 pb-1">
          <DialogTitle className="font-display text-[28px] leading-8 font-normal tracking-tight text-foreground">
            Invite someone to {workspace}
          </DialogTitle>
          <DialogDescription className="leading-5">
            They'll get an email with a link to join. It works for 7 days.
          </DialogDescription>
        </div>
        {form}
      </DialogContent>
    </Dialog>
  );
}
